import React, { useState, useContext, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Typography,
  Box,
  Tooltip,
  Chip,
  Alert,
  TextField,
  Grid,
  DialogContentText,
  TableSortLabel,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import WarningIcon from "@mui/icons-material/Warning";
import { ErpContext } from "../../context/ErpContext";

const VerLotesModal = ({ open, onClose, lotes, itemId, onUpdate }) => {
  const { updateLote, deleteLote, getLoteItemUbicacionStock } = useContext(ErpContext);

  const [editingLoteId, setEditingLoteId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [deletingLoteId, setDeletingLoteId] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  
  // Estados para stock y sorting
  const [stockPorLote, setStockPorLote] = useState({});
  const [orderBy, setOrderBy] = useState('');
  const [order, setOrder] = useState('asc');
  const [isLoadingStock, setIsLoadingStock] = useState(false);

  const formatFecha = (fecha) => {
    if (!fecha) return "-";
    return new Date(fecha).toLocaleDateString("es-AR");
  };

  const getDiasParaVencer = (fechaVencimiento) => {
    if (!fechaVencimiento) return null;
    const hoy = new Date();
    const vencimiento = new Date(fechaVencimiento);
    const diferencia = Math.ceil((vencimiento - hoy) / (1000 * 60 * 60 * 24));
    return diferencia;
  };

  const getAlertaVencimiento = (fechaVencimiento) => {
    const dias = getDiasParaVencer(fechaVencimiento);
    if (dias === null) return null;
    
    if (dias < 0) {
      return { color: "error", label: "VENCIDO", dias };
    } else if (dias <= 7) {
      return { color: "error", label: `${dias}d`, dias };
    } else if (dias <= 30) {
      return { color: "warning", label: `${dias}d`, dias };
    }
    return null;
  };

  // 🆕 Cargar stock de todos los lotes
  const cargarStockLotes = async () => {
    if (!lotes || lotes.length === 0) return;
    
    setIsLoadingStock(true);
    const stockData = {};
    
    try {
      // Cargar stock de todos los lotes en paralelo
      const promesas = lotes.map(async (lote) => {
        try {
          const stockUbicaciones = await getLoteItemUbicacionStock(lote.id);
          const stockTotal = stockUbicaciones.reduce((total, ubicacion) => {
            return total + (parseFloat(ubicacion.stock) || 0);
          }, 0);
          stockData[lote.id] = stockTotal;
        } catch (err) {
          console.warn(`Error al cargar stock del lote ${lote.id}:`, err);
          stockData[lote.id] = 0;
        }
      });
      
      await Promise.all(promesas);
      setStockPorLote(stockData);
    } catch (err) {
      console.error("Error al cargar stock de lotes:", err);
    } finally {
      setIsLoadingStock(false);
    }
  };

  // 🆕 useEffect para cargar stock cuando cambian los lotes
  useEffect(() => {
    if (open && lotes.length > 0) {
      cargarStockLotes();
    }
  }, [open, lotes]);

  // 🆕 Función para manejar sorting
  const handleSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  // 🆕 Función para ordenar los lotes
  const getSortedLotes = () => {
    if (!orderBy) return lotes;

    return [...lotes].sort((a, b) => {
      let aValue, bValue;

      switch (orderBy) {
        case 'numeroLote':
          aValue = a.numeroLote.toLowerCase();
          bValue = b.numeroLote.toLowerCase();
          break;
        case 'fechaFabricacion':
          aValue = a.fechaFabricacion ? new Date(a.fechaFabricacion) : new Date(0);
          bValue = b.fechaFabricacion ? new Date(b.fechaFabricacion) : new Date(0);
          break;
        case 'fechaVencimiento':
          aValue = a.fechaVencimiento ? new Date(a.fechaVencimiento) : new Date(0);
          bValue = b.fechaVencimiento ? new Date(b.fechaVencimiento) : new Date(0);
          break;
        case 'stockTotal':
          aValue = stockPorLote[a.id] || 0;
          bValue = stockPorLote[b.id] || 0;
          break;
        case 'observaciones':
          aValue = (a.observaciones || '').toLowerCase();
          bValue = (b.observaciones || '').toLowerCase();
          break;
        default:
          return 0;
      }

      if (aValue < bValue) {
        return order === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return order === 'asc' ? 1 : -1;
      }
      return 0;
    });
  };

  const handleEdit = (lote) => {
    setEditingLoteId(lote.id);
    setEditForm({
      numeroLote: lote.numeroLote,
      fechaFabricacion: lote.fechaFabricacion || "",
      fechaVencimiento: lote.fechaVencimiento || "",
      observaciones: lote.observaciones || "",
    });
    setError(null);
    setSuccess(null);
  };

  const handleCancelEdit = () => {
    setEditingLoteId(null);
    setEditForm({});
    setError(null);
  };

  const handleSaveEdit = async (loteId) => {
    setError(null);
    setSuccess(null);

    if (!editForm.numeroLote.trim()) {
      setError("El número de lote es requerido");
      return;
    }

    try {
      await updateLote(loteId, {
        numeroLote: editForm.numeroLote.trim(),
        fechaFabricacion: editForm.fechaFabricacion || null,
        fechaVencimiento: editForm.fechaVencimiento || null,
        observaciones: editForm.observaciones.trim() || null,
      });
      setSuccess("Lote actualizado exitosamente");
      setEditingLoteId(null);
      setEditForm({});
      onUpdate();
    } catch (err) {
      console.error("Error al actualizar lote:", err);
      setError(
        err.response?.data?.message || 
        "Error al actualizar el lote"
      );
    }
  };

  const handleDeleteClick = (loteId) => {
    setDeletingLoteId(loteId);
    setConfirmDelete(true);
    setError(null);
    setSuccess(null);
  };

  const handleConfirmDelete = async () => {
    try {
      await deleteLote(deletingLoteId);
      setSuccess("Lote eliminado exitosamente");
      setConfirmDelete(false);
      setDeletingLoteId(null);
      onUpdate();
    } catch (err) {
      console.error("Error al eliminar lote:", err);
      setError(
        err.response?.data?.message || 
        "Error al eliminar el lote"
      );
      setConfirmDelete(false);
      setDeletingLoteId(null);
    }
  };

  const handleCancelDelete = () => {
    setConfirmDelete(false);
    setDeletingLoteId(null);
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            maxHeight: "90vh",
          },
        }}
      >
        <DialogTitle
          sx={{
            backgroundColor: "#5b5b5f",
            color: "white",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="h6" component="div">
            Lotes del Item ({lotes.length})
          </Typography>
          <IconButton onClick={onClose} sx={{ color: "white" }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ mt: 2 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
              {success}
            </Alert>
          )}

          {lotes.length === 0 ? (
            <Alert severity="info">
              No hay lotes creados para este item.
            </Alert>
          ) : (
            <TableContainer component={Paper} elevation={0} sx={{ border: "1px solid #e0e0e0" }}>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                    <TableCell sx={{ fontWeight: 600 }}>
                      <TableSortLabel
                        active={orderBy === 'numeroLote'}
                        direction={orderBy === 'numeroLote' ? order : 'asc'}
                        onClick={() => handleSort('numeroLote')}
                      >
                        Número de Lote
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>
                      <TableSortLabel
                        active={orderBy === 'fechaFabricacion'}
                        direction={orderBy === 'fechaFabricacion' ? order : 'asc'}
                        onClick={() => handleSort('fechaFabricacion')}
                      >
                        Fecha Fab.
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>
                      <TableSortLabel
                        active={orderBy === 'fechaVencimiento'}
                        direction={orderBy === 'fechaVencimiento' ? order : 'asc'}
                        onClick={() => handleSort('fechaVencimiento')}
                      >
                        Fecha Venc.
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>
                      <TableSortLabel
                        active={orderBy === 'stockTotal'}
                        direction={orderBy === 'stockTotal' ? order : 'asc'}
                        onClick={() => handleSort('stockTotal')}
                      >
                        Stock Total
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Alerta</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>
                      <TableSortLabel
                        active={orderBy === 'observaciones'}
                        direction={orderBy === 'observaciones' ? order : 'asc'}
                        onClick={() => handleSort('observaciones')}
                      >
                        Observaciones
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="center">
                      Acciones
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {getSortedLotes().map((lote) => (
                    <TableRow key={lote.id} hover>
                      {editingLoteId === lote.id ? (
                        <>
                          {/* Modo edición */}
                          <TableCell>
                            <TextField
                              size="small"
                              fullWidth
                              value={editForm.numeroLote}
                              onChange={(e) =>
                                setEditForm({ ...editForm, numeroLote: e.target.value })
                              }
                              required
                            />
                          </TableCell>
                          <TableCell>
                            <TextField
                              size="small"
                              type="date"
                              fullWidth
                              value={editForm.fechaFabricacion}
                              onChange={(e) =>
                                setEditForm({
                                  ...editForm,
                                  fechaFabricacion: e.target.value,
                                })
                              }
                              InputLabelProps={{ shrink: true }}
                            />
                          </TableCell>
                          <TableCell>
                            <TextField
                              size="small"
                              type="date"
                              fullWidth
                              value={editForm.fechaVencimiento}
                              onChange={(e) =>
                                setEditForm({
                                  ...editForm,
                                  fechaVencimiento: e.target.value,
                                })
                              }
                              InputLabelProps={{ shrink: true }}
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ color: "text.secondary", fontStyle: "italic" }}>
                              {isLoadingStock ? "Cargando..." : `${(stockPorLote[lote.id] || 0).toFixed(2)}`}
                            </Typography>
                          </TableCell>
                          <TableCell>-</TableCell>
                          <TableCell>
                            <TextField
                              size="small"
                              fullWidth
                              multiline
                              rows={1}
                              value={editForm.observaciones}
                              onChange={(e) =>
                                setEditForm({
                                  ...editForm,
                                  observaciones: e.target.value,
                                })
                              }
                            />
                          </TableCell>
                          <TableCell align="center">
                            <Tooltip title="Guardar">
                              <IconButton
                                size="small"
                                onClick={() => handleSaveEdit(lote.id)}
                                sx={{ color: "green" }}
                              >
                                <SaveIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Cancelar">
                              <IconButton
                                size="small"
                                onClick={handleCancelEdit}
                                sx={{ color: "gray" }}
                              >
                                <CancelIcon />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </>
                      ) : (
                        <>
                          {/* Modo vista */}
                          <TableCell sx={{ fontWeight: 600 }}>
                            {lote.numeroLote}
                          </TableCell>
                          <TableCell>{formatFecha(lote.fechaFabricacion)}</TableCell>
                          <TableCell>{formatFecha(lote.fechaVencimiento)}</TableCell>
                          <TableCell align="right">
                            <Typography 
                              variant="body2" 
                              sx={{ 
                                fontWeight: 600,
                                color: (stockPorLote[lote.id] || 0) > 0 ? "rgb(244, 147, 14)" : "text.secondary"
                              }}
                            >
                              {isLoadingStock ? "..." : `${(stockPorLote[lote.id] || 0).toFixed(2)}`}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {(() => {
                              const alerta = getAlertaVencimiento(lote.fechaVencimiento);
                              return alerta ? (
                                <Chip
                                  icon={<WarningIcon />}
                                  label={alerta.label}
                                  color={alerta.color}
                                  size="small"
                                />
                              ) : (
                                "-"
                              );
                            })()}
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" noWrap>
                              {lote.observaciones || "-"}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Tooltip title="Editar">
                              <IconButton
                                size="small"
                                onClick={() => handleEdit(lote)}
                                sx={{ color: "rgb(244, 147, 14)" }}
                              >
                                <EditIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Eliminar">
                              <IconButton
                                size="small"
                                onClick={() => handleDeleteClick(lote.id)}
                                sx={{ color: "red" }}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 2, backgroundColor: "#f5f5f5" }}>
          <Button onClick={onClose} variant="contained">
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Diálogo de confirmación de eliminación */}
      <Dialog open={confirmDelete} onClose={handleCancelDelete}>
        <DialogTitle>Confirmar Eliminación</DialogTitle>
        <DialogContent>
          <DialogContentText>
            ¿Estás seguro de que deseas eliminar este lote? Esta acción también
            eliminará todo el stock asociado a este lote en todas las ubicaciones.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete}>Cancelar</Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained">
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default VerLotesModal;



